import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { passwordSchema } from '@bbl/validation';
import { Check } from 'lucide-react';
import { api, ApiError } from '../lib/api';

const formSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'New password must differ from current password',
    path: ['newPassword'],
  });

type FormValues = z.infer<typeof formSchema>;

export const Route = createFileRoute('/_authed/change-password')({
  component: ChangePasswordPage,
});

const TIPS: ReadonlyArray<string> = [
  'Use at least 12 characters — longer is better.',
  'Mix uppercase, lowercase, and numbers.',
  'Avoid words from your name, email, or birthday.',
  'Don’t reuse a password from another site.',
  'A short phrase you can remember is stronger than a single complex word.',
];

function ChangePasswordPage() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    setSuccess(null);
    try {
      await api.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      reset();
      setSuccess(
        "Password updated. You'll stay signed in here; other devices need to sign in again.",
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Change password
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
          Update the password you use to sign in to Babul Ilm.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium">
                Current password
              </label>
              <input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                {...register('currentPassword')}
                className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
                aria-invalid={errors.currentPassword ? 'true' : undefined}
                aria-describedby={
                  errors.currentPassword ? 'currentPassword-error' : undefined
                }
              />
              {errors.currentPassword && (
                <p id="currentPassword-error" className="mt-1 text-sm text-danger">
                  {errors.currentPassword.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium">
                New password
              </label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...register('newPassword')}
                className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
                aria-invalid={errors.newPassword ? 'true' : undefined}
                aria-describedby={
                  errors.newPassword ? 'newPassword-error' : 'newPassword-hint'
                }
              />
              {errors.newPassword ? (
                <p id="newPassword-error" className="mt-1 text-sm text-danger">
                  {errors.newPassword.message}
                </p>
              ) : (
                <p
                  id="newPassword-hint"
                  className="mt-1 text-xs text-[color:var(--color-text-muted)]"
                >
                  At least 12 characters with an uppercase letter, a lowercase letter,
                  and a number.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
                className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
                aria-invalid={errors.confirmPassword ? 'true' : undefined}
                aria-describedby={
                  errors.confirmPassword ? 'confirmPassword-error' : undefined
                }
              />
              {errors.confirmPassword && (
                <p id="confirmPassword-error" className="mt-1 text-sm text-danger">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {serverError && (
              <p role="alert" className="text-sm text-danger">
                {serverError}
              </p>
            )}

            {success && (
              <p
                role="alert"
                className="rounded-md border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-4 py-3 text-sm text-[color:var(--color-success)]"
              >
                {success}
              </p>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-md bg-ink-900 text-parchment-50 px-5 py-2.5 text-sm font-semibold hover:bg-ink-800 disabled:opacity-60"
              >
                {isSubmitting ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        </section>

        <aside className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-6">
          <h2 className="font-display text-lg font-semibold">
            Tips for a strong password
          </h2>
          <ul className="mt-4 space-y-3">
            {TIPS.map((tip) => (
              <li key={tip} className="flex gap-2 text-sm">
                <Check
                  size={16}
                  className="mt-0.5 shrink-0 text-gold-700"
                  aria-hidden="true"
                />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
